import { useState, useRef, useEffect } from 'react';
import { ApiAdimplencia } from '../../../../../services/api';
import { toast } from 'react-toastify';

/**
 * Hook responsável por controlar o estado da listagem de contratantes.
 *
 * @param {{ gestorData?: object, credorData?: object }} params
 * @returns {object}
 */
export const useContratantes = ({ gestorData, credorData }) => {
  const menu = useRef(null);

  const [selectedFilter, setSelectedFilter] = useState('contratante_numero');
  const [reload, setReload] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [contractsData, setContractsData] = useState([]);
  const [totalContracts, setTotalContracts] = useState(0);
  const [filterParam, setFilterParam] = useState('');
  const [searchState, setSearchState] = useState(false);
  const [grupoFilter, setGrupoFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ativos');
  const [gruposOptions, setGruposOptions] = useState([]);
  const [rowCurrent, setRowCurrent] = useState(null);
  const [page, setCurrentPage] = useState(0);
  const [rows, setRows] = useState(10);

  /**
   * Carrega os grupos do credor.
   *
   * @returns {Promise<void>}
   */
  const loadGrupos = async () => {
    if (!credorData?.credor_id) {
      return;
    }

    try {
      const { data } = await ApiAdimplencia.get('/credorgrupo/buscar');
      const lista = Array.isArray(data) ? data : (data?.dados || data) || [];

      const sorted = [...lista].sort((a, b) =>
          (a.grupo_descricao || '').localeCompare(b.grupo_descricao || '', 'pt-BR')
      );

      setGruposOptions([
        { label: 'Todos', value: null },
        ...sorted.map((grupo) => ({
          label: grupo.grupo_descricao,
          value: grupo.credor_grupo_id,
        })),
      ]);
    } catch {
      setGruposOptions([{ label: 'Todos', value: null }]);
    }
  };

  useEffect(() => {
    loadGrupos();
  }, [credorData?.credor_id]);

  /**
   * Monta os filtros correntes para a busca.
   *
   * @returns {Record<string, string>}
   */
  /**
   * Monta o objeto de filtros para listagem e exportação.
   * Inclui status (ativo/inativo) quando o filtro de status não for "todos".
   *
   * @returns {Record<string, string|number|boolean>}
   */
  const buildFilters = () => {
    const filters = {};

    if (searchState && filterParam) {
      filters[selectedFilter] = filterParam;
    }

    if (grupoFilter) {
      filters.credor_grupo_id = grupoFilter;
    }

    if (statusFilter === 'ativos') {
      filters.ativo = true;
    } else if (statusFilter === 'inativos') {
      filters.ativo = false;
    }

    return filters;
  };

  /**
   * Busca os contratantes com paginação e filtros.
   *
   * @param {number} [pageNum=0]
   * @param {number} [rowsNum=10]
   * @param {Record<string, string>|null} [filtersOverride=null]
   * @returns {Promise<void>}
   */
  const fetchContratantes = async (pageNum = 0, rowsNum = 10, filtersOverride = null) => {
    setIsLoading(true);

    try {
      const filters = filtersOverride ?? buildFilters();

      const params = new URLSearchParams({
        page: pageNum,
        limit: rowsNum,
        ...filters,
      }).toString();

      const { data } = await ApiAdimplencia.get(`/contratante?${params}`);
      const dados = data?.dados ?? data;
      const results = dados?.results ?? [];
      const total = dados?.total_registros ?? 0;

      setContractsData(Array.isArray(results) ? results : []);
      setTotalContracts(typeof total === 'number' ? total : 0);
    } catch (error) {
      toast.error(error?.response?.data?.mensagem || 'Erro ao carregar contratantes.');
      setContractsData([]);
      setTotalContracts(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (gestorData || credorData) {
      setCurrentPage(0);
      setFirst(0);
      fetchContratantes(0, rows);
    }
  }, [gestorData, credorData, reload, grupoFilter, statusFilter]);

  return {
    menu,
    selectedFilter,
    setSelectedFilter,
    reload,
    setReload,
    isLoading,
    first,
    setFirst,
    contractsData,
    totalContracts,
    filterParam,
    setFilterParam,
    searchState,
    setSearchState,
    grupoFilter,
    setGrupoFilter,
    statusFilter,
    setStatusFilter,
    gruposOptions,
    loadGrupos,
    buildFilters,
    rowCurrent,
    setRowCurrent,
    page,
    rows,
    setCurrentPage,
    setRows,
    fetchContratantes,
  };
};